// src/components/CallLog/DayNCallLog.jsx — Light Theme
// ─────────────────────────────────────────────────────────────────────────────
// Modal for logging Day-30 / 60 / 90 / 120 / 150 / 180 / 190 calls.
// Props:
//   open        — boolean
//   employee    — full employee object
//   callKey     — "day30" | "day60" | … | "day190"
//   onClose()   — close without save
//   onSaved()   — callback after save
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import {
  Modal, DatePicker, Select, Input, Button,
  notification, Radio, Divider, Tag,
} from "antd";
import {
  CheckCircleOutlined, SaveOutlined, PhoneOutlined,
  CalendarOutlined, UserOutlined, ClockCircleOutlined,
  WarningOutlined, SmileOutlined, FrownOutlined,
  MehOutlined, RiseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import callService from "../../services/callService";
import "./DayNCallLog.css";
import { useScheduleForCohort } from "../../context/MasterDataContext";

const C = {
  blue:"#1E6FBF", navy:"#1B2D6B", orange:"#E8871A",
  green:"#16a34a", red:"#dc2626", amber:"#b45309",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0",
};

// ── Call status options with meta ────────────────────────────────
const CALL_STATUS_OPTS = [
  { value:"Completed",     label:"Completed",     color:C.green,  bg:"#f0fdf4", border:"#86efac" },
  { value:"Not Reachable", label:"Not Reachable",  color:C.amber,  bg:"#fffbeb", border:"#fde68a" },
  { value:"Call Later",    label:"Call Later",     color:C.blue,   bg:"#eff6ff", border:"#bfdbfe" },
  { value:"No Response",        label:"No Response",         color:C.red,    bg:"#fef2f2", border:"#fca5a5" },
  { value:"Resigned",      label:"Resigned",       color:"#6b7280",bg:"#f9fafb", border:"#e5e7eb" },
];

// ── Outcome / sentiment options ──────────────────────────────────
const OUTCOME_OPTS = [
  { value:"Positive",   label:"Positive",   icon:<SmileOutlined />,  color:C.green },
  { value:"Neutral",    label:"Neutral",    icon:<MehOutlined />,    color:C.amber },
  { value:"Needs Help", label:"Needs Help", icon:<FrownOutlined />,  color:C.red   },
  { value:"At Risk",    label:"At Risk",    icon:<WarningOutlined />, color:C.red   },
  { value:"Improving",  label:"Improving",  icon:<RiseOutlined />,   color:C.blue  },
];

// ── Per-call structured prompts (optional talking points) ─────────
const CALL_PROMPTS = {
  day30: [
    "Settled into the territory / customer base?",
    "Daily planning and reporting — comfortable?",
    "Any product knowledge gaps?",
    "Manager support — adequate?",
  ],
  day60: [
    "Meeting monthly targets?",
    "Relationship with key doctors / chemists?",
    "Any system/tool issues (VCS, Axis, DB)?",
    "Morale & motivation check",
  ],
  day90: [
    "Quarter 1 performance review",
    "Clarity on growth path / incentives?",
    "Challenges with specific products?",
    "Plans for next quarter",
  ],
  day120: [
    "Mid-journey check — overall satisfaction?",
    "Any H.R. / policy concerns?",
    "Skill development needs?",
    "Peer & team dynamics",
  ],
  day150: [
    "On track for probation completion?",
    "Customer feedback & relationship quality",
    "Manager assessment alignment?",
    "Any resignation risk signals?",
  ],
  day180: [
    "Pre-confirmation review",
    "Career goals aligned with role?",
    "Training / support needed before confirmation?",
    "Overall journey satisfaction",
  ],
  day190: [
    "Journey complete — confirmation status?",
    "Long-term retention signals",
    "Key wins & achievements",
    "Handoff to regular performance cycle",
  ],
};

// ─────────────────────────────────────────────────────────────────
export default function DayNCallLog({ open, employee, callKey, onClose, onSaved }) {

  const callSchedule = useScheduleForCohort(employee?.cohortName);     // ← per-cohort schedule

  const [callDate,     setCallDate]     = useState(null);
  const [callDuration, setCallDuration] = useState(""); // minutes
  const [callStatus,   setCallStatus]   = useState("Completed");
  const [outcome,      setOutcome]      = useState("");
  const [remarks,      setRemarks]      = useState("");
  const [nextAction,   setNextAction]   = useState("");
  const [saving,       setSaving]       = useState(false);
  const [touched,      setTouched]      = useState(false);

  const schedEntry = callSchedule.find((s) => s.key === callKey);
  const callLabel  = schedEntry?.label || callKey;
  const callDay    = schedEntry?.day   || 0;
  const prompts    = CALL_PROMPTS[callKey] || [];

  // ── Pre-fill from existing call ─────────────────────────────
  useEffect(() => {
    if (!open || !employee || !callKey) return;
    const calls = employee.calls || [];
    const existing = Array.isArray(calls)
      ? (calls.find((c) => c.callKey === callKey) || {})
      : (calls?.[callKey] || {});
    setCallDate(existing.callDate     ? dayjs(existing.callDate) : dayjs());
    setCallDuration(existing.callDuration != null ? String(existing.callDuration) : "");
    setCallStatus(existing.status     || "Completed");
    setOutcome(existing.outcome       || "");
    setRemarks(existing.remarks       || "");
    setNextAction(existing.nextAction || "");
    setTouched(false);
  }, [open, employee, callKey]);

  // ── Journey context ──────────────────────────────────────────
  const doj     = employee?.dateOfJoining;
  const dueDate = doj ? dayjs(doj).add(callDay, "day") : null;
  const today   = dayjs();
  const overdue = dueDate && today.isAfter(dueDate);
  const daysOff = dueDate ? Math.abs(today.diff(dueDate, "day")) : null;

  // ── Status display ───────────────────────────────────────────
  const statusMeta = CALL_STATUS_OPTS.find((o) => o.value === callStatus);

  // ── Save ─────────────────────────────────────────────────────
  async function handleSave() {
    setTouched(true);
    if (!callDate) return;

    setSaving(true);
    try {
      const dur = parseInt(callDuration, 10);
      const payload = {
        status:       callStatus,
        callDate:     callDate.format("YYYY-MM-DD"),
        callDuration: !isNaN(dur) && dur > 0 ? dur : null,
        outcome,
        remarks,
        nextAction,
      };

      const calls = employee.calls || [];
      const existing = Array.isArray(calls)
        ? calls.find((c) => c.callKey === callKey)
        : calls?.[callKey];

      if (existing) {
        await callService.updateDayN(employee.id, callKey, payload);
      } else {
        await callService.logDayN(employee.id, callKey, payload);
      }

      notification.success({
        message:     `${callLabel} Call Saved`,
        description: `${employee.empName}'s ${callLabel} call has been recorded.`,
        placement:   "topRight",
        duration:    4,
        icon: <CheckCircleOutlined style={{ color: C.green }} />,
      });
      onSaved?.();
    } catch (err) {
      notification.error({ message: "Save failed", description: err.message });
    } finally {
      setSaving(false);
    }
  }

  const emp = employee || {};

  // ─────────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      centered
      destroyOnClose
      className="dayn-modal"
      title={null}
    >
      <div className="dayn-root">

        {/* ── Header ── */}
        <div className="dayn-header">
          <div className="dayn-header-badge">{callLabel}</div>
          <div className="dayn-header-content">
            <div className="dayn-header-title">Call Log — {callLabel}</div>
            <div className="dayn-header-sub">
              <UserOutlined style={{ marginRight: 5 }} />
              {emp.empName}
              <span className="dayn-dot">·</span>
              <span className="dayn-emp-code">{emp.empCode}</span>
              <span className="dayn-dot">·</span>
              {emp.division}
              <span className="dayn-dot">·</span>
              {emp.hq}
            </div>
          </div>

          {/* Due date chip */}
          {dueDate && (
            <div className={`dayn-due-chip ${overdue ? "dayn-due-chip--overdue" : "dayn-due-chip--ok"}`}>
              <ClockCircleOutlined />
              <span>
                {overdue
                  ? `${daysOff}d overdue`
                  : daysOff === 0
                  ? "Due today"
                  : `Due in ${daysOff}d`}
              </span>
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className="dayn-body">

          {/* Row 1: Date + Status */}
          <div className="dayn-row-2col">
            <div className="dayn-field">
              <label className="dayn-label">
                <CalendarOutlined style={{ marginRight: 5 }} />
                Call Date <span className="dayn-required">*</span>
              </label>
              <DatePicker
                value={callDate}
                onChange={(d) => setCallDate(d)}
                format="DD MMM YYYY"
                style={{ width: "100%", borderRadius: 9 }}
                status={touched && !callDate ? "error" : ""}
                placeholder="Select call date"
                disabledDate={(d) => d && d.isAfter(dayjs())}
              />
              {touched && !callDate && (
                <span className="dayn-err">Call date is required</span>
              )}
            </div>

            {/* ── Call Duration ──────────────────────────────── */}
            <div className="dayn-field">
              <label className="dayn-label" style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{
                  width:16, height:16, borderRadius:"50%",
                  background:"#1E6FBF22", display:"inline-flex",
                  alignItems:"center", justifyContent:"center", fontSize:10,
                }}>⏱</span>
                Call Duration
                <span style={{ fontSize:11, color:"#7a96b0", fontWeight:400, marginLeft:4 }}>
                  (minutes, optional)
                </span>
              </label>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <Input
                  type="number"
                  min={1}
                  max={180}
                  placeholder="e.g. 10"
                  value={callDuration}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === "" || (Number(v) >= 1 && Number(v) <= 180))
                      setCallDuration(v);
                  }}
                  style={{ borderRadius:9, width:120 }}
                  suffix={<span style={{ color:"#7a96b0", fontSize:11 }}>min</span>}
                />
                {/* Quick-select buttons */}
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {[5,10,15,20,30].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setCallDuration(String(m))}
                      style={{
                        padding:"3px 10px", borderRadius:20, fontSize:11,
                        fontWeight:700, cursor:"pointer", border:"none",
                        background: callDuration===String(m) ? "#1B2D6B" : "#EAF1F8",
                        color:      callDuration===String(m) ? "#fff"     : "#1E6FBF",
                        transition: "all .15s",
                      }}
                    >
                      {m}m
                    </button>
                  ))}
                </div>
              </div>
              {callDuration && (
                <div style={{ fontSize:11, color:"#16a34a", marginTop:4, fontWeight:600 }}>
                  ✓ {callDuration} minute{callDuration!=="1"?"s":""} recorded
                </div>
              )}
            </div>

            <div className="dayn-field">
              <label className="dayn-label">
                <PhoneOutlined style={{ marginRight: 5 }} />
                Call Status
              </label>
              <Select
                value={callStatus}
                onChange={setCallStatus}
                style={{ width: "100%", borderRadius: 9 }}
              >
                {CALL_STATUS_OPTS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    <span style={{
                      display:"inline-flex", alignItems:"center", gap:7,
                      color: o.color, fontWeight:600,
                    }}>
                      <span style={{
                        width:8, height:8, borderRadius:"50%",
                        background:o.color, display:"inline-block",
                      }}/>
                      {o.label}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Status banner */}
          {statusMeta && (
            <div className="dayn-status-banner"
              style={{ background:statusMeta.bg, borderColor:statusMeta.border, color:statusMeta.color }}>
              <span style={{ fontWeight:700 }}>{statusMeta.label}</span>
              {callStatus === "Resigned"    && " — Employee has resigned. Mark journey as closed."}
              {callStatus === "Not Reachable" && " — Schedule a follow-up call."}
              {callStatus === "No Response"       && " — Log no-response attempt; try again next cycle."}
              {callStatus === "Call Later"   && " — Record follow-up date in Next Action below."}
              {callStatus === "Completed"    && " — Call completed successfully."}
            </div>
          )}

          {/* Outcome sentiment */}
          <div className="dayn-field">
            <label className="dayn-label">Employee Sentiment / Outcome</label>
            <div className="dayn-outcome-grid">
              {OUTCOME_OPTS.map((o) => {
                const sel = outcome === o.value;
                return (
                  <button
                    key={o.value}
                    className={`dayn-outcome-btn ${sel ? "dayn-outcome-btn--sel" : ""}`}
                    style={sel ? { background:o.color+"18", borderColor:o.color, color:o.color } : {}}
                    onClick={() => setOutcome(sel ? "" : o.value)}
                  >
                    <span className="dayn-outcome-icon">{o.icon}</span>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Talking points */}
          {prompts.length > 0 && (
            <div className="dayn-prompts-wrap">
              <div className="dayn-prompts-title">
                <ClockCircleOutlined style={{ marginRight:6, color:C.blue }} />
                Suggested Talking Points
              </div>
              <div className="dayn-prompts-grid">
                {prompts.map((p, i) => (
                  <div key={i} className="dayn-prompt-item">
                    <span className="dayn-prompt-num">{i + 1}</span>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remarks */}
          <div className="dayn-field">
            <label className="dayn-label">Remarks / Observations</label>
            <Input.TextArea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder={`Key observations from the ${callLabel} call — progress, concerns, highlights…`}
              style={{ borderRadius: 10, resize:"vertical" }}
            />
          </div>

          {/* Next action */}
          <div className="dayn-field">
            <label className="dayn-label">Next Action / Follow-up</label>
            <Input
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              placeholder="e.g. Schedule call again on 15 Apr · Escalate to RM · No action needed"
              style={{ borderRadius: 9 }}
              prefix={<RiseOutlined style={{ color:C.text3 }} />}
            />
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="dayn-footer">
          <div className="dayn-footer-meta">
            {emp.rmName && (
              <span style={{ fontSize:11, color:C.text3 }}>
                RM: <strong style={{ color:C.text2 }}>{emp.rmName}</strong>
              </span>
            )}
            {dueDate && (
              <span style={{ fontSize:11, color:C.text3, marginLeft:14 }}>
                Due: <strong style={{ color: overdue ? C.red : C.text2 }}>
                  {dueDate.format("DD MMM YYYY")}
                </strong>
              </span>
            )}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <Button onClick={onClose} style={{ borderRadius:9 }}>Cancel</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              style={{ borderRadius:9, background:C.blue, minWidth:160, fontWeight:700 }}
            >
              {saving ? "Saving…" : `Save ${callLabel} Call`}
            </Button>
          </div>
        </div>

      </div>
    </Modal>
  );
}